import { Component, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EmailService, SendEmailRequest } from '../core/services/api/email.service';
import { MessageService } from 'primeng/api';
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { commonService } from '@bl-src/app/core/services/common.service';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent {
  /**
   * Reactive form group for the contact form fields.
   * Holds name, email, optional phone, and message with basic validation.
   */
  public contactForm: FormGroup = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required), // sender full name
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]), // sender email address
    phone: new FormControl<any | null>(null), // optional phone; validated by ngx-intl when present
    message: new FormControl<string | null>(null, [ // message body
      Validators.required,
      Validators.maxLength(250),
    ]),
  });

  // Signals for component state
  public readonly isSubmitting = signal(false); // indicates in-flight request state
  public readonly submitSuccess = signal(false); // last submission ended successfully
  public readonly submitError = signal(false); // last submission ended with error
  public readonly statusMessage = signal(''); // latest status/detail message for user
  public readonly hasTriedSubmit = signal(false); // tracks first submit attempt to control UI feedback

  // Signals reflecting form control values and validity
  private readonly nameCtrl = this.contactForm.get('name') as FormControl; // control: name
  private readonly emailCtrl = this.contactForm.get('email') as FormControl; // control: email
  private readonly phoneCtrl = this.contactForm.get('phone') as FormControl; // control: phone
  private readonly messageCtrl = this.contactForm.get('message') as FormControl; // control: message

  public readonly nameValue = toSignal(this.nameCtrl.valueChanges, { initialValue: this.nameCtrl.value }); // signal: name value
  public readonly emailValue = toSignal(this.emailCtrl.valueChanges, { initialValue: this.emailCtrl.value }); // signal: email value
  public readonly phoneValue = toSignal(this.phoneCtrl.valueChanges, { initialValue: this.phoneCtrl.value }); // signal: phone object
  public readonly messageValue = toSignal(this.messageCtrl.valueChanges, { initialValue: this.messageCtrl.value }); // signal: message value

  public preferredCountries: CountryISO[] = [ // top country suggestions for phone input
    CountryISO.SaudiArabia,
    CountryISO.Egypt,
  ];

  public selectedCountryISO: CountryISO = CountryISO.SaudiArabia; // default selected country

  public searchCountryField: SearchCountryField[] = [ // search fields for country picker
    SearchCountryField.Iso2,
    SearchCountryField.Name,
  ];

  public numberFormat: PhoneNumberFormat = PhoneNumberFormat.International; // display international format
  
  /**
   * Indicates invalid state for the optional phone control.
   * Returns false when empty (optional), otherwise mirrors control invalid state.
   */
  private get isPhoneInvalidOptional(): boolean {
    const ctrl = this.contactForm.get('phone');
    if (!ctrl) return false;
    const value = ctrl.value;
    if (!value) return false; // optional when empty
    return ctrl.invalid;
  }
  
  constructor(
    private translate: TranslateService,
    private emailService: EmailService,
    private messageService: MessageService,
    private commonService: commonService
  ) {}

  /**
   * Handle form submission lifecycle:
   * - Validate form and phone control
   * - Build payload (includes contextual subject and dial-code CountryId)
   * - Call API and surface success/error via toast
   */
  public onSubmit(): void {
    this.hasTriedSubmit.set(true);
    this.submitSuccess.set(false);
    this.submitError.set(false);
    this.statusMessage.set('');

    if (this.contactForm.invalid || this.isPhoneInvalidOptional) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const branchName = this.commonService.getTokenObject?.BranchNameEn || undefined; // branch name from tokenObject
    const accountId = this.commonService.getTokenObject?.AccountSetupId || undefined; // account id from tokenObject
    const emailSubject = `Subscription Support Request with (${branchName ?? ''}${accountId && branchName ? ' - ' : ''}${accountId ?? ''})`;
    const payload: SendEmailRequest = {
      SenderName: this.nameValue() ?? '',
      SenderEmail: this.emailValue() ?? '',
      MobileNumber: this.phoneValue()?.internationalNumber || undefined,
      Message: this.messageValue() ?? '',
      Subject: emailSubject,
      CountryId: this.phoneValue()?.dialCode ?? undefined,
    };

    this.isSubmitting.set(true);
    this.statusMessage.set(this.translate.instant('translate_sending'));

    this.emailService
      .sendEmail(payload)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.isSubmitting.set(false);
          const isSuccess = res && Number(res.State) === 100;
          if (isSuccess) {
            this.submitSuccess.set(true);
            const detail = res?.Message || this.translate.instant('translate_contact_success');
            this.statusMessage.set(detail);
            this.messageService.add({ severity: 'success', summary: this.translate.instant('translate_success'), detail });
            this.contactForm.reset();
          } else {
            this.submitError.set(true);
            const detail = res?.Message || this.translate.instant('translate_contact_error');
            this.statusMessage.set(detail);
            this.messageService.add({ severity: 'error', summary: this.translate.instant('translate_error'), detail });
          }
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.submitError.set(true);
          const detail = err?.error?.Message || this.translate.instant('translate_contact_error');
          this.statusMessage.set(detail);
          this.messageService.add({ severity: 'error', summary: this.translate.instant('translate_error'), detail });
        },
      });
  }
}
